import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react'
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Paper,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  alpha,
  Tooltip,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import StopIcon from '@mui/icons-material/Stop'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import RefreshIcon from '@mui/icons-material/Refresh'
import AirIcon from '@mui/icons-material/Air'
import ExtensionIcon from '@mui/icons-material/Extension'
import BubbleChartIcon from '@mui/icons-material/BubbleChart'
import CloseIcon from '@mui/icons-material/Close'
import HeadphonesIcon from '@mui/icons-material/Headphones'
import { toast } from 'react-toastify'
import {
  getPatientEngagement,
  savePatientEngagement,
  resetPatientEngagement,
  getBackgroundMusicTracks,
  ENGAGEMENT_GAME_OPTIONS,
} from 'src/supportPortal/services/patientEngagementService'

// Lazy load the music player from consumer portal
const SoothingMusicPlayer = lazy(() => 
  import('src/consumer/components/PublicLiveSessionPage/components/WaitingEngagement/music/SoothingMusicPlayer')
)

const SectionBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  border: '1px solid #e2e8f0',
  marginBottom: theme.spacing(2),
}))

const GameChip = styled(Chip)(({ theme, selected }) => ({
  margin: theme.spacing(0.5),
  borderColor: selected ? '#9c27b0' : '#e2e8f0',
  backgroundColor: selected ? alpha('#9c27b0', 0.1) : 'transparent',
  color: selected ? '#9c27b0' : 'inherit',
  '&:hover': {
    backgroundColor: selected ? alpha('#9c27b0', 0.15) : alpha('#9c27b0', 0.05),
  },
}))

const PreviewButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: theme.spacing(0.5, 1),
  fontSize: '11px',
  borderRadius: theme.spacing(0.75),
}))

const gameIcons = {
  breathing: <AirIcon sx={{ fontSize: 16 }} />,
  memory: <ExtensionIcon sx={{ fontSize: 16 }} />,
  bubbles: <BubbleChartIcon sx={{ fontSize: 16 }} />,
}

const gameLabels = {
  breathing: 'Breathing Exercise',
  memory: 'Memory Game',
  bubbles: 'Pop Bubbles',
}

/**
 * EngagementTabContent - Tab content for patient engagement settings
 * Shows current settings, allows editing, and provides previews
 */
export default function EngagementTabContent({ patient, onRefresh }) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [engagementData, setEngagementData] = useState(null)
  const [musicTracks, setMusicTracks] = useState([])
  const [tracksLoading, setTracksLoading] = useState(false)

  // Edit form state
  const [enabled, setEnabled] = useState(false)
  const [selectedTracks, setSelectedTracks] = useState([])
  const [selectedGames, setSelectedGames] = useState([])

  // Preview state
  const [previewDialog, setPreviewDialog] = useState({ open: false, type: null, data: null })
  const [musicPlayerOpen, setMusicPlayerOpen] = useState(false)
  const [audioPlaying, setAudioPlaying] = useState(null)
  const [audioElement, setAudioElement] = useState(null)

  // Load engagement data
  const loadEngagementData = useCallback(async () => {
    if (!patient?.id) return

    setLoading(true)
    try {
      const result = await getPatientEngagement(patient.id)
      if (result?.success && result.config) {
        const data = result.config
        setEngagementData(data)
        
        // Initialize form state from data
        setEnabled(data?.enabled || false)
        setSelectedTracks(data?.engagement?.music?.tracks?.map(t => t.id) || [])
        setSelectedGames(data?.engagement?.games?.available || [])
      } else {
        // No engagement config exists
        setEngagementData(null)
        setEnabled(false)
        setSelectedTracks([])
        setSelectedGames([])
      }
    } catch (error) {
      console.error('Error loading engagement data:', error)
      // Use data from patient object if API fails
      if (patient?.waitingScreenEngagement) {
        setEngagementData(patient.waitingScreenEngagement)
        setEnabled(true)
        setSelectedTracks(patient.waitingScreenEngagement?.music?.tracks?.map(t => t.id) || [])
        setSelectedGames(patient.waitingScreenEngagement?.games?.available || [])
      }
    } finally {
      setLoading(false)
    }
  }, [patient])

  // Load available music tracks
  const loadMusicTracks = useCallback(async () => {
    setTracksLoading(true)
    try {
      const result = await getBackgroundMusicTracks(true)
      if (result?.success && Array.isArray(result.tracks)) {
        setMusicTracks(result.tracks)
      } else {
        setMusicTracks([])
      }
    } catch (error) {
      console.error('Error loading music tracks:', error)
      setMusicTracks([])
    } finally {
      setTracksLoading(false)
    }
  }, [])

  useEffect(() => {
    loadEngagementData()
    loadMusicTracks()
  }, [loadEngagementData, loadMusicTracks])

  // Handle save
  const handleSave = async () => {
    setSaving(true)
    try {
      const engagement = {
        music: {
          tracks: selectedTracks.map(trackId => {
            const track = musicTracks.find(t => t.id === trackId)
            return {
              id: track?.id || trackId,
              name: track?.name || 'Unknown Track',
              mediaId: track?.mediaId || null,
            }
          }),
        },
        games: {
          available: selectedGames,
        },
      }

      const result = await savePatientEngagement(patient.id, { enabled, engagement })
      if (result?.success) {
        toast.success('Engagement settings saved')
        setEditMode(false)
        loadEngagementData()
        onRefresh?.()
      } else {
        toast.error(result?.message || 'Failed to save settings')
      }
    } catch (error) {
      console.error('Error saving engagement:', error)
      toast.error('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  // Handle reset
  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset engagement settings to default?')) return

    setSaving(true)
    try {
      const result = await resetPatientEngagement(patient.id)
      if (result?.success) {
        toast.success('Engagement settings reset')
        setEditMode(false)
        loadEngagementData()
        onRefresh?.()
      } else {
        toast.error(result?.message || 'Failed to reset settings')
      }
    } catch (error) {
      console.error('Error resetting engagement:', error)
      toast.error('Failed to reset settings')
    } finally {
      setSaving(false)
    }
  }

  // Handle game toggle
  const toggleGame = (gameId) => {
    setSelectedGames(prev => 
      prev.includes(gameId) 
        ? prev.filter(g => g !== gameId)
        : [...prev, gameId]
    )
  }

  // Play/Stop music preview
  const toggleMusicPreview = (track) => {
    if (audioPlaying === track.id) {
      // Stop current track
      audioElement?.pause()
      setAudioPlaying(null)
      setAudioElement(null)
    } else {
      // Stop any playing track
      audioElement?.pause()
      
      // Play new track
      const mediaUrl = track.mediaUrl || track.url
      if (mediaUrl) {
        const audio = new Audio(mediaUrl)
        audio.play().catch(e => console.error('Audio play error:', e))
        audio.onended = () => {
          setAudioPlaying(null)
          setAudioElement(null)
        }
        setAudioElement(audio)
        setAudioPlaying(track.id)
      } else {
        toast.info('Preview not available for this track')
      }
    }
  }

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      audioElement?.pause()
    }
  }, [audioElement])

  // Open game preview
  const openGamePreview = (gameType) => {
    setPreviewDialog({ open: true, type: 'game', data: gameType })
  }

  // Get track details by ID
  const getTrackById = (trackId) => {
    return musicTracks.find(t => t.id === trackId)
  }

  // Check if engagement is configured
  const hasEngagement = engagementData?.enabled || patient?.waitingScreenEngagement

  // Get display data (from API response or patient object)
  const displayData = engagementData || patient?.waitingScreenEngagement

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} sx={{ color: '#9c27b0' }} />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SportsEsportsIcon sx={{ color: '#9c27b0' }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Waiting Screen Engagement
          </Typography>
          {hasEngagement && (
            <Chip 
              label="Enabled" 
              size="small" 
              sx={{ 
                height: 20, 
                fontSize: '10px',
                backgroundColor: alpha('#9c27b0', 0.15),
                color: '#9c27b0',
                fontWeight: 600,
              }} 
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {editMode ? (
            <>
              <Button
                size="small"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setEditMode(false)
                  // Reset form to original values
                  setEnabled(engagementData?.enabled || false)
                  setSelectedTracks(engagementData?.engagement?.music?.tracks?.map(t => t.id) || [])
                  setSelectedGames(engagementData?.engagement?.games?.available || [])
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ backgroundColor: '#9c27b0', '&:hover': { backgroundColor: '#7b1fa2' } }}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={loadEngagementData}>
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => setEditMode(true)}
                sx={{ color: '#9c27b0' }}
              >
                Edit
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Status Info */}
      {!hasEngagement && !editMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No engagement settings configured for this patient. Click Edit to set up waiting screen activities.
        </Alert>
      )}

      {/* Edit Mode */}
      {editMode ? (
        <>
          {/* Enable/Disable Toggle */}
          <SectionBox>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#9c27b0' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#9c27b0' },
                  }}
                />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  Enable waiting screen engagement
                </Typography>
              }
            />
          </SectionBox>

          {enabled && (
            <>
              {/* Music Selection */}
              <SectionBox>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <MusicNoteIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600}>Background Music</Typography>
                </Box>
                
                {tracksLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Music Tracks</InputLabel>
                    <Select
                      multiple
                      value={selectedTracks}
                      onChange={(e) => setSelectedTracks(e.target.value)}
                      input={<OutlinedInput label="Select Music Tracks" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((trackId) => {
                            const track = getTrackById(trackId)
                            return (
                              <Chip
                                key={trackId}
                                label={track?.name || trackId}
                                size="small"
                                sx={{ height: 22, fontSize: '11px' }}
                              />
                            )
                          })}
                        </Box>
                      )}
                    >
                      {musicTracks.map((track) => (
                        <MenuItem key={track.id} value={track.id}>
                          <Checkbox checked={selectedTracks.includes(track.id)} size="small" />
                          <ListItemText primary={track.name} secondary={track.artist} />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleMusicPreview(track)
                            }}
                            sx={{ ml: 1 }}
                          >
                            {audioPlaying === track.id ? (
                              <StopIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
                            ) : (
                              <PlayArrowIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </SectionBox>

              {/* Games Selection */}
              <SectionBox>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <SportsEsportsIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600}>Games & Activities</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {ENGAGEMENT_GAME_OPTIONS.map((game) => (
                    <GameChip
                      key={game.value}
                      icon={gameIcons[game.value]}
                      label={game.label}
                      variant="outlined"
                      selected={selectedGames.includes(game.value)}
                      onClick={() => toggleGame(game.value)}
                      clickable
                    />
                  ))}
                </Box>

                {selectedGames.length > 0 && (
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                      Preview:
                    </Typography>
                    {selectedGames.map((gameId) => (
                      <PreviewButton
                        key={gameId}
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon sx={{ fontSize: 12 }} />}
                        onClick={() => openGamePreview(gameId)}
                        sx={{ color: '#9c27b0', borderColor: '#9c27b0' }}
                      >
                        {gameLabels[gameId]}
                      </PreviewButton>
                    ))}
                  </Box>
                )}
              </SectionBox>

              {/* Reset Button */}
              <Button
                size="small"
                color="error"
                onClick={handleReset}
                disabled={saving}
              >
                Reset to Default
              </Button>
            </>
          )}
        </>
      ) : (
        /* View Mode */
        hasEngagement && (
          <>
            {/* Music Display */}
            <SectionBox>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MusicNoteIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600}>Background Music</Typography>
                  {displayData?.engagement?.music?.tracks?.length > 0 && (
                    <Chip 
                      label={`${displayData.engagement.music.tracks.length} tracks`} 
                      size="small" 
                      sx={{ height: 18, fontSize: '10px', backgroundColor: alpha('#9c27b0', 0.1), color: '#9c27b0' }} 
                    />
                  )}
                </Box>
                {displayData?.engagement?.music?.tracks?.length > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<HeadphonesIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setMusicPlayerOpen(true)}
                    sx={{ 
                      fontSize: '11px', 
                      py: 0.5,
                      backgroundColor: '#9c27b0',
                      '&:hover': { backgroundColor: '#7b1fa2' },
                    }}
                  >
                    Play Music
                  </Button>
                )}
              </Box>
              
              {displayData?.engagement?.music?.tracks?.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {displayData.engagement.music.tracks.map((track, idx) => (
                    <Chip
                      key={track.id || idx}
                      icon={<MusicNoteIcon sx={{ fontSize: 14 }} />}
                      label={track.name}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: alpha('#9c27b0', 0.3) }}
                      onDelete={track.mediaId ? () => toggleMusicPreview(track) : undefined}
                      deleteIcon={
                        audioPlaying === track.id ? (
                          <StopIcon sx={{ fontSize: 14, color: '#9c27b0' }} />
                        ) : (
                          <PlayArrowIcon sx={{ fontSize: 14 }} />
                        )
                      }
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No music tracks configured</Typography>
              )}
            </SectionBox>

            {/* Games Display */}
            <SectionBox>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SportsEsportsIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={600}>Games & Activities</Typography>
              </Box>
              
              {displayData?.engagement?.games?.available?.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {displayData.engagement.games.available.map((gameId) => (
                    <Tooltip key={gameId} title="Click to preview">
                      <Chip
                        icon={gameIcons[gameId]}
                        label={gameLabels[gameId] || gameId}
                        size="small"
                        variant="outlined"
                        onClick={() => openGamePreview(gameId)}
                        sx={{ 
                          borderColor: alpha('#9c27b0', 0.3),
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: alpha('#9c27b0', 0.05) },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No games configured</Typography>
              )}
            </SectionBox>
          </>
        )
      )}

      {/* Music Player Dialog */}
      <Dialog
        open={musicPlayerOpen}
        onClose={() => {
          setMusicPlayerOpen(false)
          // Stop any playing audio when closing
          audioElement?.pause()
          setAudioPlaying(null)
          setAudioElement(null)
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HeadphonesIcon />
            <Typography variant="h6">Music Player</Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={() => setMusicPlayerOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#fafafa' }}>
          {displayData?.engagement?.music?.tracks?.length > 0 ? (
            <Suspense fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#9c27b0' }} />
              </Box>
            }>
              <SoothingMusicPlayer tracks={displayData.engagement.music.tracks} />
            </Suspense>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <MusicNoteIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
              <Typography color="text.secondary">No music tracks available</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Game Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, type: null, data: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {gameIcons[previewDialog.data]}
            <Typography variant="h6">{gameLabels[previewDialog.data]} Preview</Typography>
          </Box>
          <IconButton size="small" onClick={() => setPreviewDialog({ open: false, type: null, data: null })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {previewDialog.data === 'breathing' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  backgroundColor: alpha('#9c27b0', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                  animation: 'breathe 4s ease-in-out infinite',
                  '@keyframes breathe': {
                    '0%, 100%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.3)' },
                  },
                }}
              >
                <AirIcon sx={{ fontSize: 48, color: '#9c27b0' }} />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Guided breathing exercise to help patients relax while waiting
              </Typography>
            </Box>
          )}
          
          {previewDialog.data === 'memory' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      backgroundColor: alpha('#9c27b0', 0.1 + (i * 0.05)),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'scale(1.05)' },
                    }}
                  >
                    <ExtensionIcon sx={{ color: '#9c27b0', fontSize: 24 }} />
                  </Box>
                ))}
              </Box>
              <Typography variant="body1" color="text.secondary">
                Memory card matching game to keep patients engaged
              </Typography>
            </Box>
          )}
          
          {previewDialog.data === 'bubbles' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                {[30, 45, 35, 50, 40].map((size, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: size,
                      height: size,
                      borderRadius: '50%',
                      background: `radial-gradient(circle at 30% 30%, ${alpha('#9c27b0', 0.3)}, ${alpha('#9c27b0', 0.6)})`,
                      animation: `float ${2 + i * 0.3}s ease-in-out infinite`,
                      '@keyframes float': {
                        '0%, 100%': { transform: 'translateY(0)' },
                        '50%': { transform: 'translateY(-10px)' },
                      },
                    }}
                  />
                ))}
              </Box>
              <Typography variant="body1" color="text.secondary">
                Fun bubble popping game for stress relief
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}
